/**
 * Minimal PostHog client helper.
 *
 * PostHog is loaded and initialised by the Blazor host page (see index.html there),
 * so we only forward events to the global `trackPostHogEvent` when it exists.
 */
export function trackPostHogEvent(event: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.trackPostHogEvent) {
    return;
  }

  try {
    window.trackPostHogEvent(event, properties);
  } catch (e) {
    console.error('[PostHog] tracking failed', e);
  }
}

const trackedOnce = new Set<string>();

/**
 * Like trackPostHogEvent, but sends the event only once per key and page load
 * (e.g. once per project for events triggered by autosave).
 */
export function trackPostHogEventOnce(
  key: string,
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (trackedOnce.has(key)) {
    return;
  }
  trackedOnce.add(key);
  trackPostHogEvent(event, properties);
}

const STEP_VIEW_DEDUP_MS = 2000;
let lastStepView: { key: string; at: number } | null = null;

/**
 * Sends "bep_step_viewed" unless the same step of the same project was just sent. The editor can
 * remount when Blazor re-renders a step, which would otherwise count the same view twice.
 */
export function trackStepViewed(
  projectId: string,
  properties: { step: string } & Record<string, unknown>,
): void {
  const key = `${projectId}:${properties.step}`;
  const now = Date.now();
  if (lastStepView && lastStepView.key === key && now - lastStepView.at < STEP_VIEW_DEDUP_MS) {
    return;
  }
  lastStepView = { key, at: now };
  trackPostHogEvent('bep_step_viewed', properties);
}

/**
 * Adds properties to every following PostHog event (from React and Blazor) until cleared.
 */
export function setPostHogContext(properties: Record<string, unknown>): void {
  window.setPostHogContext?.(properties);
}

export function clearPostHogContext(keys: string[]): void {
  window.clearPostHogContext?.(keys);
}
